/**
 * Games Framework Module
 * 
 * This module provides the core infrastructure for building modular games
 * in the familie app. It includes:
 * 
 * - Game registry and definitions
 * - Session management for game plays
 * - User statistics and leaderboards
 * - Weekend competition integration
 * - Challenge system for versus games
 * - Team notifications for point scoring
 * 
 * @example
 * ```typescript
 * import { useGameRegistry, useGameSession, useGameChallenge, GameCard } from '@/modules/games';
 * 
 * // Get available games
 * const { weekendGames, freePlayGames } = useGameRegistry();
 * 
 * // Start a game session
 * const { createSession, session } = useGameSession();
 * await createSession({ game_slug: 'quiz', mode: 'weekend', session_type: 'solo' });
 * 
 * // Send a challenge
 * const { sendChallenge, receivedChallenges } = useGameChallenge();
 * await sendChallenge({ game_slug: 'tic_tac_toe', challenged_id: 'user-id', mode: 'free' });
 * ```
 */

// Types
export * from './types';

// Hooks
export { useGameRegistry } from './hooks/useGameRegistry';
export { useGameSession } from './hooks/useGameSession';
export { useGameStats, useSingleGameStats } from './hooks/useGameStats';
export { useGameChallenge } from './hooks/useGameChallenge';
export { useActiveGames, useActiveGamesBySlug } from './hooks/useActiveGames';
export { useFreePlayLeaderboard, useAllGamesLeaderboard } from './hooks/useFreePlayLeaderboard';

// Components
export { GameCard, GameCardCompact } from './components/GameCard';
export { Leaderboard } from './components/Leaderboard';
export { GameModeSelector } from './components/GameModeSelector';
export { SendChallengeModal } from './components/ChallengeModal';
export { ChallengeBanner } from './components/ChallengeBanner';
export { ActiveGameCard } from './components/ActiveGameCard';
export { RematchModal } from './components/RematchModal';

// Utilities
export { 
  notifyTeamOfPoints, 
  notifyTeamOfGameStart,
  notifyAllUsersOfScore,
  sendRematchNotification 
} from './utils/teamNotifications';

// Game slug constants
export const GAME_SLUGS = {
  QUIZ: 'quiz',
  SPEURTOCHT: 'speurtocht',
  TIC_TAC_TOE: 'tic_tac_toe',
  CHESS: 'chess',
  PUZZLE_MATCHING: 'puzzle_matching',
  SUDOKU: 'sudoku',
  MATCH_3: 'match_3',
  ENDLESS_RUNNER: 'endless_runner',
} as const;

export type GameSlug = typeof GAME_SLUGS[keyof typeof GAME_SLUGS];

// Memory game image registry from assets
export const MEMORY_GAME_IMAGES = [
  'airplace.jpg',
  'friendly_blue_monster.jpg',
  'funny_monster.jpg',
  'minion_hero.jpg',
  'minion_muscle.jpg',
  'purple_monther.jpg',
  'r2d2.jpg',
  'robot.jpg',
  'spce.jpg',
  'spiderman.jpg',
  'sweet_monster.jpg',
  'toystory.jpg',
  'yellow_pumpkin.jpg',
  'yellow.jpg',
] as const;

